import { useQuery } from '@tanstack/react-query'

const API_BASE = '/api/dashboard'

/**
 * Query keys factory for dashboard
 * Ensures consistent cache keys across all hooks
 */
export const DASHBOARD_KEYS = {
  all: ['dashboard'] as const,
  metrics: () => [...DASHBOARD_KEYS.all, 'metrics'] as const,
  recent: () => [...DASHBOARD_KEYS.all, 'recent'] as const,
}

/**
 * Dashboard metrics response type
 */
export interface DashboardMetrics {
  contacts: number
  companies: number
  deals: {
    total: number
    active: number
    won: number
    conversionRate: number
    totalValue: number
    revenue: number
  }
  pipeline: {
    stage: string
    count: number
    value: number
  }[]
}

/**
 * Fetch dashboard metrics
 * Returns aggregated counts and stats for contacts, companies, and deals
 */
export function useDashboardMetrics() {
  return useQuery({
    queryKey: DASHBOARD_KEYS.metrics(),
    queryFn: async (): Promise<DashboardMetrics> => {
      const response = await fetch(`${API_BASE}/metrics`, {
        credentials: 'include', // Include session cookie
      })
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard metrics')
      }
      return response.json()
    },
    // Refetch dashboard metrics every 30 seconds to keep data fresh
    refetchInterval: 30000,
    // Keep stale data while refetching
    staleTime: 15000,
  })
}

/**
 * Recent activity item types
 */
export interface RecentDeal {
  id: string
  title: string
  stage: string
  value: number
  createdAt: Date
  updatedAt: Date
}

export interface RecentCase {
  id: string
  subject: string
  status: string
  priority: string
  createdAt: Date
  updatedAt: Date
}

export interface RecentContact {
  id: string
  firstName: string
  lastName: string
  email: string | null
  createdAt: Date
  updatedAt: Date
}

export interface RecentCompany {
  id: string
  name: string
  industry: string | null
  createdAt: Date
  updatedAt: Date
}

export interface RecentEnquiry {
  id: string
  name: string
  email: string
  company: string | null
  status: string
  formType: string
  createdAt: Date
  updatedAt: Date
}

export interface RecentTask {
  id: string
  title: string
  status: string
  priority: string
  dueAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export interface RecentActivity {
  deals: RecentDeal[]
  cases: RecentCase[]
  contacts: RecentContact[]
  companies: RecentCompany[]
  enquiries: RecentEnquiry[]
  tasks: RecentTask[]
}

/**
 * Fetch recent activity
 * Returns latest items from deals, cases, contacts, companies, and enquiries
 */
export function useRecentActivity() {
  return useQuery({
    queryKey: DASHBOARD_KEYS.recent(),
    queryFn: async (): Promise<RecentActivity> => {
      const response = await fetch(`${API_BASE}/recent`, {
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error('Failed to fetch recent activity')
      }
      return response.json()
    },
    // Refetch recent activity every 60 seconds
    refetchInterval: 60000,
    staleTime: 30000,
  })
}
